import { CartItemsComponent } from '@theme/component-cart-items';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * One-click discount offer for the cart drawer.
 *
 * Applies the code through the same cart/update.js + section-rendering flow as
 * `cart-discount.js`. Success is only shown once Shopify allocates an amount for the
 * code; the rendered state itself comes from Liquid, so quantity changes and removals
 * recheck it on every cart re-render.
 */
class CartPromoOffer extends HTMLElement {
  /** Shared across instances because a morph can replace the element mid-request. */
  static #pending = false;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-promo-apply]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#apply(button);
  };

  /** @param {HTMLButtonElement} button */
  async #apply(button) {
    if (CartPromoOffer.#pending) return;

    const { sectionId, code } = this.dataset;
    if (!sectionId || !code) return;

    CartPromoOffer.#pending = true;
    const restoreButton = this.#setBusy(button);
    this.#showMessage('');
    /** @type {string[] | null} Codes to put back if a replacement fails midway. */
    let replacedCodes = null;

    try {
      // Let in-flight quantity updates settle so the code list we send is current.
      let pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
      while (pendingQuantityUpdates.length) {
        await Promise.all(pendingQuantityUpdates);
        pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
      }

      const current = CartPromoOffer.#current() ?? this;
      /** @type {string[]} */
      const existingCodes = JSON.parse(current.dataset.existingCodes || '[]');
      const matchesCode = (/** @type {unknown} */ value) =>
        typeof value === 'string' && value.toUpperCase() === code.toUpperCase();

      if (existingCodes.some(matchesCode)) {
        // Already on the cart: re-render so the applied state reflects Shopify's data.
        await this.#render(sectionId);
        return;
      }

      // In the replace state the shopper saw that this code replaces their current one,
      // so send it alone; otherwise keep any other codes alongside it.
      const replacing = current.dataset.replace === 'true' && existingCodes.length > 0;
      if (replacing) replacedCodes = existingCodes;
      const data = await this.#updateDiscounts(sectionId, replacing ? [code] : [...existingCodes, code]);

      if (CartPromoOffer.#savedAmount(data, matchesCode) > 0) {
        replacedCodes = null;
        await this.#render(sectionId, data);
        return;
      }

      // Not applied: restore the shopper's previous codes so their original discount stays active.
      const stillListed = (data.discount_codes ?? []).some((/** @type {{ code: string }} */ d) => matchesCode(d.code));
      const restored = replacing || stillListed ? await this.#updateDiscounts(sectionId, existingCodes) : data;
      replacedCodes = null;
      await this.#render(sectionId, restored);

      let messageKey = existingCodes.length ? 'errorIncompatible' : 'errorInvalid';
      if (replacing) messageKey = 'errorRestored';
      this.#showMessage(CartPromoOffer.#current()?.dataset[messageKey] ?? this.dataset[messageKey] ?? '');
      this.dispatchEvent(new CartErrorEvent({ error: `Discount ${code} not applied`, code: 'VALIDATION_CUSTOM' }));
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      // Best effort: never leave the shopper without their original discount.
      if (replacedCodes) {
        try {
          await this.#render(sectionId, await this.#updateDiscounts(sectionId, replacedCodes));
        } catch {
          // The generic error below still tells the shopper to try again.
        }
      }

      this.#showMessage(CartPromoOffer.#current()?.dataset.errorGeneric ?? this.dataset.errorGeneric ?? '');
      this.dispatchEvent(
        new CartErrorEvent({
          error: (error instanceof Error && error.message) || 'Failed to apply discount',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      CartPromoOffer.#pending = false;
      restoreButton();
    }
  }

  /**
   * Sets the cart's discount codes, announcing the change like `cart-discount.js` does.
   * @param {string} sectionId
   * @param {string[]} codes
   */
  async #updateDiscounts(sectionId, codes) {
    sectionRenderer.abortRender(sectionId);
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: codes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', { body: JSON.stringify({ discount: codes.join(','), sections: [sectionId] }) })
      );
      if (!response.ok) throw new Error(`Cart update failed (${response.status})`);

      const data = await response.json();
      deferredPromise.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });
      return data;
    } catch (error) {
      deferredPromise.reject(error);
      throw error;
    }
  }

  /**
   * Morphs the drawer with the returned section HTML, or fetches a fresh render.
   * @param {string} sectionId
   * @param {{ sections?: Record<string, string> | null }} [data]
   */
  async #render(sectionId, data) {
    const mode = this.closest('theme-drawer') ? 'hydration' : 'full';
    const html = data?.sections?.[sectionId];

    sectionRenderer.abortRender(sectionId);
    if (html) {
      await morphSection(sectionId, html, { mode });
    } else {
      await sectionRenderer.renderSection(sectionId, { cache: false, mode });
    }
  }

  /**
   * Disables the button without removing focus, mirroring Horizon's discount controls.
   * @param {HTMLButtonElement} button
   */
  #setBusy(button) {
    const label = button.textContent;
    button.setAttribute('aria-disabled', 'true');
    this.setAttribute('aria-busy', 'true');
    if (button.dataset.labelBusy) button.textContent = button.dataset.labelBusy;

    return () => {
      this.removeAttribute('aria-busy');
      if (!button.isConnected) return;
      button.removeAttribute('aria-disabled');
      button.textContent = label;
    };
  }

  /** @param {string} message */
  #showMessage(message) {
    const element = (CartPromoOffer.#current() ?? this).querySelector('[data-promo-message]');
    if (!(element instanceof HTMLElement)) return;

    element.textContent = message;
    element.hidden = !message;
  }

  /** The offer element currently in the DOM, which may differ from `this` after a morph. */
  static #current() {
    const element = document.querySelector('cart-promo-offer');
    return element instanceof HTMLElement ? element : null;
  }

  /**
   * Sums what Shopify actually allocated for the code in a cart AJAX response.
   * @param {any} cart
   * @param {(value: unknown) => boolean} matchesCode
   */
  static #savedAmount(cart, matchesCode) {
    let total = 0;
    // The AJAX cart nests cart-level entries as { amount, discount_application: { title, ... } }.
    for (const entry of cart.cart_level_discount_applications ?? []) {
      const application = entry.discount_application ?? entry;
      if (matchesCode(application.title)) {
        total += Number(entry.amount ?? application.total_allocated_amount) || 0;
      }
    }
    for (const item of cart.items ?? []) {
      for (const allocation of item.line_level_discount_allocations ?? []) {
        if (matchesCode(allocation.discount_application?.title)) total += Number(allocation.amount) || 0;
      }
    }
    return total;
  }
}

if (!customElements.get('cart-promo-offer')) {
  customElements.define('cart-promo-offer', CartPromoOffer);
}
